import { useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { useTenantDevices, useCreateDevice } from "@/api/devices";
import { usePagination } from "@/hooks/use-pagination";
import { useTenantContext } from "@/hooks/use-tenant-context";
import { DataTable } from "@/components/data-table/data-table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ComplianceBadge } from "@/components/compliance-badge";
import { SourceStatusPill } from "@/components/source-status-pill";
import { exportDevicesCSV } from "@/api/reports";
import { useAuth } from "@/stores/auth-store";
import { canManageDevices } from "@/lib/roles";
import type { Device } from "@/api/types";

export function DeviceListPage() {
  const { user } = useAuth();
  const { tenantId, tenant } = useTenantContext();
  const { page, pageSize, search, params, setPage, setPageSize, setSearch, setFilter } = usePagination();
  const { data, isLoading } = useTenantDevices(tenantId!, params);
  const [dialogOpen, setDialogOpen] = useState(false);

  const columns: ColumnDef<Device, unknown>[] = [
    {
      accessorKey: "hostname",
      header: "Hostname",
      cell: ({ row }) => (
        <Link to={`/tenants/${tenantId}/devices/${row.original.id}`} className="font-medium text-accent hover:underline">
          {row.original.hostname || row.original.nickname || "Unnamed"}
        </Link>
      ),
    },
    { accessorKey: "platform", header: "Platform" },
    { accessorKey: "device_type", header: "Type" },
    {
      accessorKey: "compliance_score",
      header: "Compliance",
      cell: ({ row }) => <ComplianceBadge score={row.original.compliance_score} />,
    },
    {
      id: "sources",
      header: "Sources",
      cell: ({ row }) => {
        const summary = row.original.source_summary || {};
        return (
          <div className="flex gap-1">
            <SourceStatusPill source="crowdstrike" linked={!!summary.crowdstrike} />
            <SourceStatusPill source="atera" linked={!!summary.atera} />
            <SourceStatusPill source="jumpcloud" linked={!!summary.jumpcloud} />
          </div>
        );
      },
    },
    { accessorKey: "last_seen", header: "Last Seen", cell: ({ row }) => row.original.last_seen ? new Date(row.original.last_seen).toLocaleDateString() : "Never" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{tenant?.name ? `${tenant.name} — Devices` : "Devices"}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exportDevicesCSV(tenantId!, params)}>Export CSV</Button>
          {canManageDevices(user) && <Button onClick={() => setDialogOpen(true)}>Create Device</Button>}
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search devices..."
        page={page}
        pageSize={pageSize}
        totalPages={data?.meta?.totalPages ?? 1}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        total={data?.meta?.total}
        toolbar={
          <div className="flex gap-2">
            <Select onChange={(e) => setFilter("compliance_tier", e.target.value || null)} className="w-36">
              <option value="">All Tiers</option>
              <option value="compliant">Compliant</option>
              <option value="at_risk">At Risk</option>
              <option value="non_compliant">Non-Compliant</option>
              <option value="unscored">Unscored</option>
            </Select>
            <Select onChange={(e) => setFilter("platform", e.target.value || null)} className="w-32">
              <option value="">All Platforms</option>
              <option value="windows">Windows</option>
              <option value="macos">macOS</option>
              <option value="linux">Linux</option>
            </Select>
            <Select onChange={(e) => setFilter("has_source", e.target.value || null)} className="w-36">
              <option value="">All Sources</option>
              <option value="crowdstrike">CrowdStrike</option>
              <option value="atera">Atera</option>
              <option value="jumpcloud">JumpCloud</option>
            </Select>
            <Select onChange={(e) => setFilter("has_user", e.target.value || null)} className="w-40">
              <option value="">All Devices</option>
              <option value="true">With User</option>
              <option value="false">Without User</option>
            </Select>
          </div>
        }
      />
      {dialogOpen && <CreateDeviceDialog tenantId={tenantId!} open={dialogOpen} onClose={() => setDialogOpen(false)} />}
    </div>
  );
}

function CreateDeviceDialog({ tenantId, open, onClose }: { tenantId: string; open: boolean; onClose: () => void }) {
  const createDevice = useCreateDevice(tenantId);
  const [form, setForm] = useState({ hostname: "", serial_number: "", platform: "", device_type: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createDevice.mutate(form, { onSuccess: () => { onClose(); } });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create Device</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div><label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Hostname</label><Input value={form.hostname} onChange={(e) => setForm({ ...form, hostname: e.target.value })} required /></div>
        <div><label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Serial Number</label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
        <div><label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Platform</label>
          <Select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
            <option value="">Select...</option><option value="windows">Windows</option><option value="macos">macOS</option><option value="linux">Linux</option>
          </Select>
        </div>
        <div><label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Device Type</label>
          <Select value={form.device_type} onChange={(e) => setForm({ ...form, device_type: e.target.value })}>
            <option value="">Select...</option><option value="workstation">Workstation</option><option value="laptop">Laptop</option><option value="server">Server</option>
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={createDevice.isPending}>{createDevice.isPending ? "Creating..." : "Create"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
